import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
  Bank as BankIcon,
  Check as CheckIcon,
  Clock as ClockIcon,
  PencilSimple as EditIcon,
  User as UserIcon,
  X as XIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from '@/components/core/v2/toaster';

import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import type { AmenityReservation } from '@/types/v2/reservation';
import { approveReservation, getPendingReservations, rejectReservation, updateCommunityReservation } from '@/api/reservations';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { hasAnyAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';

export function PendingReservations(): React.JSX.Element {
  const { account, community } = useUser();
  const queryClient = useQueryClient();
  const rejectDialog = useDialog();

  const [selectedReservation, setSelectedReservation] = React.useState<AmenityReservation | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
  const [reservationToApprove, setReservationToApprove] = React.useState<AmenityReservation | null>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editErrors, setEditErrors] = React.useState<Record<string, string>>({});
  const [editForm, setEditForm] = React.useState({
    start_time: '',
    end_time: '',
    purpose: '',
    special_requests: '',
    guest_count: '',
  });

  const canEditReservations =
    hasAnyAccountRoles(account!, [
      ACCOUNT_ROLES.ACCOUNT_OWNER,
      ACCOUNT_ROLES.ACCOUNT_ADMIN,
      ACCOUNT_ROLES.ACCOUNT_MANAGER,
    ]) ||
    hasCommunityRolesV2(community, [
      COMMUNITY_ROLES.COMMUNITY_MANAGER,
      COMMUNITY_ROLES.BOARD_MEMBER,
    ]);

  const { data, isLoading } = useQuery({
    queryKey: ['pending-reservations', community?.id],
    queryFn: () => getPendingReservations(community?.id || ''),
    enabled: !!community?.id,
  });

  const approveMutation = useMutation({
    mutationFn: ({ reservationId }: { reservationId: string }) =>
      approveReservation(community?.id || '', reservationId, {}),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success('Reservation approved successfully');
    },
    onError: (error: any) => {
      console.error('Approval error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to approve reservation';
      toast.error(errorMessage);

      // If the reservation is not found, refresh the list
      if (errorMessage.includes('not found') || errorMessage.includes('null')) {
        queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ reservationId, data }: { reservationId: string; data: any }) =>
      updateCommunityReservation(community?.id || '', reservationId, data),
    onError: (error: any) => {
      const responseData = error?.response?.data;
      if (error?.response?.status === 422 && responseData?.errors) {
        setEditErrors(responseData.errors);
        toast.error('Please fix the validation errors below');
      } else {
        setEditErrors({});
        toast.error(responseData?.message || 'Failed to update reservation');
      }
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ reservationId, reason }: { reservationId: string; reason: string }) =>
      rejectReservation(community?.id || '', reservationId, { rejected_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      toast.success('Reservation rejected');
      rejectDialog.handleClose();
      setSelectedReservation(null);
      setRejectReason('');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to reject reservation');
    },
  });

  const handleApprove = (reservation: AmenityReservation) => {
    // Check if reservation is still in pending status
    if (reservation.status !== 'pending') {
      toast.error('This reservation is no longer pending');
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      return;
    }

    setReservationToApprove(reservation);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = () => {
    if (reservationToApprove) {
      approveMutation.mutate({ reservationId: reservationToApprove.id });
    }
    setApproveDialogOpen(false);
    setReservationToApprove(null);
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setReservationToApprove(null);
  };

  const handleRejectClick = (reservation: AmenityReservation) => {
    setSelectedReservation(reservation);
    rejectDialog.handleOpen();
  };

  const handleEditClick = (reservation: AmenityReservation) => {
    setSelectedReservation(reservation);
    setEditForm({
      start_time: dayjs.utc(reservation.start_time).local().format('YYYY-MM-DD HH:mm:ss'),
      end_time: dayjs.utc(reservation.end_time).local().format('YYYY-MM-DD HH:mm:ss'),
      purpose: reservation.purpose || '',
      special_requests: reservation.special_requests || '',
      guest_count: reservation.guest_count?.toString() || '',
    });
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const getEditPayload = (reservation: AmenityReservation) => {
    const data: any = {};
    if (editForm.start_time !== dayjs.utc(reservation.start_time).local().format('YYYY-MM-DD HH:mm:ss')) {
      data.start_time = dayjs(editForm.start_time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    if (editForm.end_time !== dayjs.utc(reservation.end_time).local().format('YYYY-MM-DD HH:mm:ss')) {
      data.end_time = dayjs(editForm.end_time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    if (editForm.purpose !== reservation.purpose) data.purpose = editForm.purpose;
    if (editForm.special_requests !== reservation.special_requests) data.special_requests = editForm.special_requests;
    if (editForm.guest_count !== reservation.guest_count?.toString()) {
      data.guest_count = editForm.guest_count ? parseInt(editForm.guest_count) : null;
    }
    return data;
  };

  const handleEditSubmit = async ({ approveAfterSave = false }: { approveAfterSave?: boolean } = {}) => {
    if (!selectedReservation) return;
    const reservationId = selectedReservation.id;
    const data = getEditPayload(selectedReservation);

    try {
      if (Object.keys(data).length > 0) {
        await updateMutation.mutateAsync({ reservationId, data });
      }
      if (approveAfterSave) {
        await approveMutation.mutateAsync({ reservationId });
        toast.success('Reservation updated and approved');
      } else if (Object.keys(data).length > 0) {
        toast.success('Reservation updated successfully');
      } else {
        toast.info('No reservation changes to save');
      }
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      setEditDialogOpen(false);
      setSelectedReservation(null);
    } catch {
      // Mutation handlers show the concrete failure.
    }
  };

  const handleRejectSubmit = () => {
    if (!selectedReservation) return;
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    rejectMutation.mutate({
      reservationId: selectedReservation.id,
      reason: rejectReason,
    });
  };

  const pendingReservations = data?.pending_reservations || [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pendingReservations.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <BankIcon color="var(--mui-palette-text-disabled)" size={48} style={{ marginBottom: 12 }} />
        <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body1">
          No pending reservations
        </Typography>
        <Typography color="text.disabled" sx={{ mt: 0.5 }} variant="body2">
          All reservations have been processed.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        {pendingReservations.map((reservation) => (
          <Box
            key={reservation.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
            }}
          >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'warning.main',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BankIcon size={24} weight="duotone" color="white" />
                  </Box>
                  <Box>
                    <Typography variant="h6">{reservation.amenity?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requested {dayjs(reservation.created_at).fromNow()}
                    </Typography>
                  </Box>
                </Box>
                <Chip label="PENDING APPROVAL" size="small" color="warning" sx={{ fontWeight: 600 }} />
              </Stack>

              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UserIcon size={18} />
                  <Typography variant="body2">
                    {reservation.user?.first_name} {reservation.user?.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({reservation.user?.email})
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ClockIcon size={18} />
                  <Typography variant="body2">
                    {dayjs.utc(reservation.start_time).local().format('MMM D, YYYY h:mm A')} -{' '}
                    {dayjs.utc(reservation.end_time).local().format('h:mm A')}
                  </Typography>
                </Box>

                {reservation.purpose && (
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Purpose:</strong> {reservation.purpose}
                    </Typography>
                  </Box>
                )}

                {reservation.special_requests && (
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Special Requests:</strong> {reservation.special_requests}
                    </Typography>
                  </Box>
                )}

                {reservation.alcohol_served && (
                  <Typography variant="caption" color="text.secondary">
                    Alcohol will be served
                  </Typography>
                )}

                {reservation.guest_count && (
                  <Typography variant="caption" color="text.secondary">
                    Guests: {reservation.guest_count}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                {canEditReservations && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon size={18} />}
                    onClick={() => handleEditClick(reservation)}
                    disabled={dayjs.utc(reservation.start_time).local().isBefore(dayjs())}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckIcon size={18} />}
                  onClick={() => handleApprove(reservation)}
                  disabled={approveMutation.isPending}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<XIcon size={18} />}
                  onClick={() => handleRejectClick(reservation)}
                  disabled={rejectMutation.isPending}
                >
                  Reject
                </Button>
              </Stack>
          </Box>
        ))}
      </Stack>

      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Pending Reservation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DateTimePicker
              label="Start Time"
              value={dayjs(editForm.start_time)}
              onChange={(newValue) => {
                if (newValue) {
                  setEditForm((prev) => ({ ...prev, start_time: newValue.format('YYYY-MM-DD HH:mm:ss') }));
                  if (editErrors.start_time) setEditErrors((prev) => ({ ...prev, start_time: '' }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(editErrors.start_time),
                  helperText: editErrors.start_time,
                },
              }}
            />
            <DateTimePicker
              label="End Time"
              value={dayjs(editForm.end_time)}
              onChange={(newValue) => {
                if (newValue) {
                  setEditForm((prev) => ({ ...prev, end_time: newValue.format('YYYY-MM-DD HH:mm:ss') }));
                  if (editErrors.end_time) setEditErrors((prev) => ({ ...prev, end_time: '' }));
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(editErrors.end_time),
                  helperText: editErrors.end_time,
                },
              }}
            />
            <TextField
              label="Purpose"
              multiline
              rows={2}
              value={editForm.purpose}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, purpose: event.target.value }));
                if (editErrors.purpose) setEditErrors((prev) => ({ ...prev, purpose: '' }));
              }}
              fullWidth
              error={Boolean(editErrors.purpose)}
              helperText={editErrors.purpose}
            />
            <TextField
              label="Special Requests"
              multiline
              rows={3}
              value={editForm.special_requests}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, special_requests: event.target.value }));
                if (editErrors.special_requests) setEditErrors((prev) => ({ ...prev, special_requests: '' }));
              }}
              fullWidth
              error={Boolean(editErrors.special_requests)}
              helperText={editErrors.special_requests}
            />
            <TextField
              label="Guest Count"
              type="number"
              value={editForm.guest_count}
              onChange={(event) => {
                setEditForm((prev) => ({ ...prev, guest_count: event.target.value }));
                if (editErrors.guest_count) setEditErrors((prev) => ({ ...prev, guest_count: '' }));
              }}
              fullWidth
              inputProps={{ min: 0 }}
              error={Boolean(editErrors.guest_count)}
              helperText={editErrors.guest_count}
            />
            {selectedReservation && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Resident:</strong> {selectedReservation.user?.first_name} {selectedReservation.user?.last_name}
                  <br />
                  <strong>Amenity:</strong> {selectedReservation.amenity?.name}
                  <br />
                  <strong>Final time:</strong> {dayjs(editForm.start_time).format('MMM D, YYYY h:mm A')} - {dayjs(editForm.end_time).format('h:mm A')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              void handleEditSubmit();
            }}
            variant="outlined"
            disabled={updateMutation.isPending || approveMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            onClick={() => {
              void handleEditSubmit({ approveAfterSave: true });
            }}
            variant="contained"
            color="success"
            disabled={updateMutation.isPending || approveMutation.isPending}
          >
            {updateMutation.isPending || approveMutation.isPending ? 'Saving...' : 'Save & Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialog.open} onClose={rejectDialog.handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Reservation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this reservation. The resident will be notified.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Reason for Rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., The amenity is undergoing maintenance during this time..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={rejectDialog.handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={rejectMutation.isPending || !rejectReason.trim()}
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject Reservation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleApproveCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'success.lighter',
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckIcon size={22} weight="bold" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Approve reservation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confirm the final details before notifying the resident.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {reservationToApprove && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.default',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {reservationToApprove.amenity?.name || 'Reservation'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {reservationToApprove.user?.first_name} {reservationToApprove.user?.last_name}
                      {reservationToApprove.user?.email ? ` · ${reservationToApprove.user.email}` : ''}
                    </Typography>
                  </Box>
                  <Chip label="Pending" color="warning" size="small" sx={{ fontWeight: 700 }} />
                </Stack>

                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClockIcon size={18} />
                    <Typography variant="body2">
                      {dayjs.utc(reservationToApprove.start_time).local().format('MMM D, YYYY')}
                      {' · '}
                      {dayjs.utc(reservationToApprove.start_time).local().format('h:mm A')} -{' '}
                      {dayjs.utc(reservationToApprove.end_time).local().format('h:mm A')}
                    </Typography>
                  </Box>

                  {reservationToApprove.guest_count ? (
                    <Typography variant="body2" color="text.secondary">
                      Guests: {reservationToApprove.guest_count}
                    </Typography>
                  ) : null}

                  {reservationToApprove.purpose ? (
                    <Typography variant="body2" color="text.secondary">
                      Purpose: {reservationToApprove.purpose}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'success.lighter',
                  color: 'success.dark',
                }}
              >
                <Typography variant="body2">
                  Approving this reservation will change its status to approved and make it visible to the resident as confirmed.
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleApproveCancel} variant="outlined">
            Keep Pending
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? 'Approving...' : 'Approve Reservation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}





